/**
 * useFarmerSurfaces — web port of the independent-farmer (G1) navigation
 * visibility rule from the mobile farmer-home screen
 * (apps/mobile/app/farmer-home.tsx, lines ~44-90).
 *
 * The home/nav surface for a `role='farmer'` user shows two data surfaces
 * conditionally: "My harvests" (the farmer's own harvest log) and "My
 * contributions" (deliveries into a cooperative's batch). Which one is the
 * ACTIVE surface depends on whether the farmer belongs to a cooperative, and
 * the inactive one is still shown when there's a historical log worth viewing.
 * This hook computes those two booleans so the web nav/home can mirror mobile
 * exactly. It owns no UI — consumers read the four flags it returns.
 *
 * Symmetric visibility rule (identical to mobile):
 *   - isInCoop          = farm-profile cooperativeId !== null.
 *   - showHarvests      = !isInCoop || hasHarvests
 *       An independent farmer ALWAYS sees My harvests (it's their active
 *       surface). A coop member sees it only if they have past harvests.
 *   - showContributions = isInCoop || hasPastContributions
 *       The inverse: a coop member ALWAYS sees My contributions (active
 *       surface); an independent farmer sees it only if they have past
 *       coop deliveries (e.g. they left a coop and the log is now read-only).
 *
 * Fail-open posture: the GET /me/farm-profile probe 404s for non-farmer
 * accounts (coop_admin/admin) and may error offline. Rather than hiding the
 * farmer's own surfaces on any failure, we default `isInCoop=false` (the
 * common G1 case) whenever the farm-profile query is pending or errored. The
 * worst case is showing My harvests + My contributions to someone who has
 * neither — which the empty states handle gracefully — instead of hiding a
 * real surface from a real farmer.
 *
 * Probe optimisation (mirrors mobile — only fetch what's needed to decide):
 *   - The contributions count only matters when NOT in a coop (in a coop,
 *     showContributions is true regardless), so the contributions probe is
 *     enabled only when `!!sub && !isInCoop`.
 *   - The harvests count only matters when IN a coop (independent farmers
 *     always see harvests), so the harvests probe is enabled only when
 *     `isInCoop`.
 *   - Both gates depend on `isInCoop`, which derives from the farm-profile
 *     query. While that query is pending, `isInCoop=false`, so the
 *     contributions probe runs (correct for the independent default) and the
 *     harvests probe stays idle — the expected ordering.
 *
 * Cache-key sharing: every query key below is identical to the one used by the
 * standalone screen that owns that data, so nothing double-fetches:
 *   - ['me-farm-profile']                         (the farm-profile read)
 *   - ['my-contributions', { farmerUserId: sub }] (_app.my-contributions.tsx)
 *   - ['my-harvests']                             (the harvests list page)
 *
 * The three reads go through the generated orval clients
 * (./generated/me/me, ./generated/contributions/contributions,
 * ./generated/harvests/harvests) — the hand-written manual stubs this hook
 * once imported were removed by the orval regen.
 */
import { useQuery } from '@tanstack/react-query';

import { readAccessTokenClaims } from './client';
import { contributionsControllerList } from './generated/contributions/contributions';
import { harvestsControllerList } from './generated/harvests/harvests';
import { meControllerGetFarmProfile } from './generated/me/me';

export interface FarmerSurfaces {
  /** Still resolving the farm-profile / data probes. Consumers may render the
   *  independent-default (harvests shown, contributions hidden) meanwhile. */
  isLoading: boolean;
  /** cooperativeId !== null. Fail-open to false on 404 / error. */
  isInCoop: boolean;
  /** !isInCoop || hasHarvests */
  showHarvests: boolean;
  /** isInCoop || hasPastContributions */
  showContributions: boolean;
}

export function useFarmerSurfaces(enabled = true): FarmerSurfaces {
  const sub = readAccessTokenClaims()?.sub;

  // Farm profile — the gate. `retry: false` so a 404 (non-farmer) or offline
  // error resolves to the fail-open default immediately rather than retrying.
  // `enabled` lets a non-farmer caller (the shared sidebar renders for every
  // role) skip the farmer-scoped probes entirely — see the early return below.
  const farmProfileQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    retry: false,
    enabled: enabled && !!sub,
  });

  // Fail-open: only treat the farmer as in-coop when the profile resolved AND
  // carries a cooperativeId. Pending / errored → false (independent default).
  const isInCoop = farmProfileQuery.data?.cooperativeId != null;

  // Contributions presence probe — only needed when independent (in a coop the
  // contributions surface is shown regardless). Shares the my-contributions
  // screen's cache key so that page renders instantly.
  const contributionsQuery = useQuery({
    queryKey: ['my-contributions', { farmerUserId: sub }],
    queryFn: ({ signal }) => contributionsControllerList({ farmerUserId: sub! }, signal),
    enabled: enabled && !!sub && !isInCoop,
  });

  // Harvests presence probe — only needed when in a coop (independent farmers
  // always see harvests). Shares the harvests list page's cache key.
  const harvestsQuery = useQuery({
    queryKey: ['my-harvests'],
    queryFn: ({ signal }) => harvestsControllerList(undefined, signal),
    enabled: enabled && isInCoop,
  });

  const showHarvests = !isInCoop || (harvestsQuery.data?.data?.length ?? 0) > 0;
  const showContributions =
    isInCoop || (contributionsQuery.data?.data?.length ?? 0) > 0;

  // Disabled (non-farmer) caller: report a stable, surface-free default. The
  // nav role-filter already drops the farmer-only items for these roles, so
  // these values are inert — this just avoids a disabled query's perpetual
  // `isPending` leaking out as `isLoading: true`.
  if (!enabled) {
    return { isLoading: false, isInCoop: false, showHarvests: false, showContributions: false };
  }

  return {
    // The farm-profile pending state is the primary gate; the enabled probe
    // that's actually running for the current branch refines it.
    isLoading:
      farmProfileQuery.isPending ||
      (contributionsQuery.fetchStatus !== 'idle' && contributionsQuery.isPending) ||
      (harvestsQuery.fetchStatus !== 'idle' && harvestsQuery.isPending),
    isInCoop,
    showHarvests,
    showContributions,
  };
}
